import styled, { css } from 'styled-components';

import { colors, layout } from 'src/components/base';
import { BodySmallText, CaptionText } from '@prisjakt/ui/text/variants';
import { CardActionArea } from '@prisjakt/ui/card';
import { Tooltip } from '@prisjakt/ui/tooltip';

import { ViewSize } from '../../types';
import type { ITableLinkProps } from '../../types';

type SharedProps = {
  $featured?: boolean;
  $hasMemberPrice?: boolean;
  $viewSize?: ViewSize;
};

type AlternativeRowProps = {
  $isAlternative?: boolean;
};

export const StyledFeaturedRow = styled(CardActionArea)`
  flex: 1 1 auto;
  cursor: pointer;
  overflow: visible;
`;

export const StyledProductNameWrapper = styled.div<
  SharedProps & { $alignStart?: boolean; $paddingTop?: boolean }
>(
  props => css`
    display: grid;
    flex-shrink: 1;
    height: 100%;
    align-items: center;
    max-width: 60%;

    ${props.$alignStart &&
    css`
      align-items: flex-start;
    `}

    ${props.$alignStart &&
    props.$paddingTop &&
    css`
      padding-top: ${layout.measurements.spacing050};
    `}
  `
);

export const StyledProductName = styled(BodySmallText)<
  SharedProps & AlternativeRowProps
>(
  props => css`
    white-space: initial;
    color: ${props.$featured
      ? props.theme.text.primary
      : props.theme.text.tertiary};
    display: inline;

    line-height: 22px;

    ${props.$isAlternative &&
    css`
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    `}
  `
);

export const StyledTopMiddleRow = styled.div<SharedProps & AlternativeRowProps>`
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: space-between;
  flex: 1;
  padding: 0 ${layout.measurements.spacing050};
  overflow: hidden;

  ${props =>
    props.$viewSize !== undefined &&
    props.$viewSize >= ViewSize.MEDIUM &&
    css`
      padding: ${layout.measurements.spacing025};
      gap: 0;
    `}
`;

export const StyledMiddleColumn = styled.div<SharedProps>(
  props => css`
    display: flex;
    align-items: center;
    flex-grow: 1;
    height: 100%;
    width: 100%;

    ${props.$featured &&
    css`
      &:hover::after {
        box-shadow: 0 2px 10px 0 rgb(0, 0, 0, 0.1), 0 0 0 1px rgb(0, 0, 0, 0);
      }
      ::after {
        transition: box-shadow 200ms ease;
        content: '';
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        pointer-events: none;
        border-radius: 8px;
      }
    `}
  `
);

export const StyledEndColumn = styled.div<SharedProps>(
  props => css`
    display: flex;
    flex-shrink: 0;
    align-items: stretch;
    height: 100%;
    padding: ${layout.measurements.spacing050};
    padding-left: 0;

    ${props.$viewSize === ViewSize.MEDIUM &&
    css`
      padding: ${layout.measurements.spacing050};
    `}

    ${props.$viewSize === ViewSize.LARGE &&
    css`
      padding-left: ${layout.measurements.spacing050};
      padding-right: ${layout.measurements.spacing100};
    `}

    /* Small mobile resolutions, e.g. Galaxy Fold */
    @media screen and (max-width: 280px) {
      padding-right: ${layout.measurements.spacing100};
    }

    ${!props.$featured &&
    css`
      visibility: hidden;
    `}
  `
);

export const StyledUnfeaturedRow = styled.a<ITableLinkProps>(
  props => css`
    display: flex;
    flex: 2;
    align-items: center;

    ${!props.featured &&
    css`
      cursor: default;
      gap: ${layout.measurements.spacing025};
    `}

    &:target {
      outline: ${colors.borderLightGray} dotted 1px;
      outline: -webkit-focus-ring-color auto 1px;
    }
  `
);

export const StyledCollapseUnfeaturedBtn = styled(Tooltip)<SharedProps>(
  props => css`
    )cursor: pointer;
    position: absolute;
    display: flex;
    align-self: center;
    right: 0;

    ${props.$viewSize === ViewSize.MEDIUM &&
    css`
      right: ${layout.measurements.spacing050};
    `}

    ${props.$viewSize === ViewSize.LARGE &&
    css`
      right: ${layout.measurements.spacing100};
    `}
  `
);

export const StyledFlexHorizontal = styled.div`
  display: flex;
`;

export const StyledFlexVertical = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  justify-content: center;
  white-space: nowrap;
`;

export const StyledMembershipWrapper = styled(StyledFlexHorizontal)`
  justify-content: flex-end;
`;

export const StyledMembershipTextWrapper = styled(CaptionText)`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

export const StyledMembershipText = styled.span`
  font-style: italic;
  margin-left: ${layout.measurements.spacing025};
  margin-right: ${layout.measurements.spacing050};
  white-space: break-spaces;

  /* Small mobile resolutions, e.g. Galaxy Fold */
  @media screen and (max-width: 280px) {
    margin-left: 0;
  }
`;

export const StyledMembershipBadgeWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${layout.measurements.spacing050};
`;

export const StyledMiddleColumnMobilePadding = styled(
  StyledFlexVertical
)<SharedProps>(
  props => css`
    ${props.$viewSize === ViewSize.SMALL &&
    css`
      padding: ${layout.measurements.spacing050} 0;
    `}

    &:hover::after {
      box-shadow: 0 2px 10px 0 rgb(0, 0, 0, 0.1), 0 0 0 1px rgb(0, 0, 0, 0);
    }
    ::after {
      transition: box-shadow 200ms ease;
      content: '';
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      pointer-events: none;
      border-radius: 8px;
    }
  `
);

export const StyledMiddleColumnDesktopPadding = styled(
  StyledFlexVertical
)<SharedProps>`
  padding: 0 ${layout.measurements.spacing050};

  ${props =>
    props.$viewSize !== undefined &&
    props.$viewSize >= ViewSize.MEDIUM &&
    css`
      padding: ${layout.measurements.spacing050};
    `}
`;
